<template>
  <div class="workshop-list">
    <div
      class="workshop-list__mobile"
      id="workshop-list-mobile"
    >
      <Carousel
        v-if="currentWorkshop"
        :mouse-drag="workshops.length > 1"
        :touch-drag="workshops.length > 1"
        :modelValue="currentWorkshop.index"
        ref="listCarousel"
        @slide-end="onSlideEnd"
      >
        <Slide
          v-for="(workshop, index) in workshops"
          :index="index"
          :key="workshop.id"
        >
          <Workshop
            @click="emits('show-workshop', workshop)"
            :workshop="workshop"
            tabindex="0"
          />
        </Slide>
      </Carousel>
      <div
        class="workshop-list__mobile-no-workshops"
        v-else-if="!loading && !workshops.length && searchInitLoad"
      >
        <NuxtImg
          src="/_nuxt/no-result.png"
          alt="Brak warsztatów"
          height="71"
          width="101"
        />
        <div>
          <h4 class="font-weight-500"><span class="color-primary-blue">Ups... </span>nie znaleźliśmy wasztatów</h4>
          <p class="color-grey-3">Spróbuj poszukać warsztatu w innym rejonie lub zwiększyć zasięg wyszukiwania na mapie.
          </p>
        </div>
      </div>

    </div>
    <div
      class="workshop-list__desktop"
      id="workshop-list-desktop"
    >
      <div class="workshop-list__desktop-wrapper">
        <Workshop
          v-if="workshops.length"
          :id="`workshop.id:${workshop.id}`"
          v-for="workshop in workshops"
          :key="workshop.id"
          :workshop="workshop"
          :currentWorkshop="currentWorkshop?.id === workshop.id"
          @click="emits('show-workshop', workshop)"
          @mouseover="emits('mouse-over', workshop)"
          @mouseleave="emits('mouse-leave', workshop)"
        />
        <div
          v-else-if="!loading && !workshops.length && searchInitLoad"
          class="workshop-list__desktop-no-workshops"
        >
          <NuxtImg
            src="/_nuxt/no-result.png"
            height="180"
            width="260"
          />
          <div>
            <h4 class="font-weight-500"><span class="color-primary-blue">Ups... </span>nie znaleźliśmy wasztatów</h4>
            <p class="color-grey-3">Spróbuj poszukać warsztatu w innym rejonie lub zwiększyć zasięg wyszukiwania na
              mapie.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script
  setup
  lang="ts"
>
import { Carousel, Slide } from 'vue3-carousel';
import Workshop from '@/components/Map/Workshop.vue';
import { type IWorkshop, type IWorkshopExtended } from '@/pages/mapa/index.vue';
import { onClickOutside } from '@vueuse/core';

interface IProps {
  workshops: IWorkshopExtended[];
  currentWorkshop: IWorkshop | IWorkshopExtended | null;
  loading: boolean;
  searchInitLoad: boolean;
}

interface IEmits {
  (name: 'show-workshop', value: IWorkshopExtended): void;
  (name: 'show-on-map', value: IWorkshopExtended): void;
  (name: 'mouse-over', value: IWorkshopExtended): void;
  (name: 'mouse-leave', value: IWorkshopExtended): void;
  (name: 'reset-current-workshop'): void;
}

const emits = defineEmits<IEmits>();

const props = defineProps<IProps>();

// ractive
const listCarousel = ref(null);

onClickOutside(listCarousel, () => {
  emits('reset-current-workshop');
});

// watch
watch(() => props.currentWorkshop, (workshop: IWorkshop | IWorkshopExtended | null) => {
  if (workshop) {
    scrollToSelectedWorkshop(workshop);
  }
});

// methods
const scrollToSelectedWorkshop = (workshop: IWorkshop | IWorkshopExtended) => {
  const isMobile = window.innerWidth <= 768;

  const scrollToSelectedWorkshopDesktop = (workshop: IWorkshop | IWorkshopExtended) => {
    const workshopElement = document.getElementById(`workshop.id:${workshop.id}`);
    const workshopList = document.getElementById('workshop-list-desktop');

    if (!workshopElement || !workshopList) return;
    const y = workshopElement.offsetTop - 20;

    workshopList.scroll({
      top: y,
      behavior: 'smooth'
    });
  };

  const scrollToSelectedWorkshopMobile = (workshop: IWorkshop | IWorkshopExtended) => {
    if (workshop && listCarousel.value) {
      (listCarousel.value as any).slideTo(workshop.index);
    }
  };

  if (isMobile) {
    scrollToSelectedWorkshopMobile(workshop);
  } else {
    scrollToSelectedWorkshopDesktop(workshop);
  }
};

const onSlideEnd = (e: any) => {
  const foundWorkshop = props.workshops.find(workshop => {
    return workshop.index === e.currentSlideIndex;
  });

  if (!foundWorkshop) return;
  emits('show-on-map', foundWorkshop);
}

</script>

<style lang="scss">
.workshop-list {
  &__mobile {
    z-index: 5;

    @media (min-width: $breakpoint) {
      display: none;
    }
  }

  &__desktop {
    display: none;

    @media (min-width: $breakpoint) {
      display: block;
      overflow-y: auto;
      height: calc(100vh - 70px);
      box-sizing: border-box;
      padding: 20px;
    }

    @media (min-width: 1280px) {
      padding: 32px;
    }
  }

  &__desktop-wrapper {
    display: flex;
    flex-direction: column;
    gap: 25px;
  }

  &__mobile-no-workshops {
    background-color: $white;
    padding: 12px 28px;
    display: flex;
    flex-direction: column;
    align-items: center;
    border-radius: 10px;
    margin: 0 8px;

    h4 {
      margin: 12px 0 8px 0;
      text-align: center;
    }

    p {
      margin: 0;
      text-align: center;
    }
  }

  &__desktop-no-workshops {
    display: flex;
    flex-direction: column;
    align-items: center;

    h4 {
      margin: 22px 0 12px 0;
      text-align: center;
      font-size: 20px;
      line-height: 28px;
    }

    p {
      margin: 0;
      text-align: center;
    }
  }
}

.carousel__slide {
  padding: 0;
  height: 100%;
}

.carousel__track {
  margin: 0;
}
</style>